import { MonitorHeart, MonitorHeartOutlined } from '@mui/icons-material';
import { DrawerLayout, UsersIcon, useLocale } from 'shared-ui';

import { ReactComponent as DevicesIcon } from 'assets/icons/devices.svg';
import { ReactComponent as DevicesActiveIcon } from 'assets/icons/devices-active.svg';
import { ReactComponent as CardsIcon } from 'assets/icons/cards.svg';
import { ReactComponent as CardsActiveIcon } from 'assets/icons/cards-active-drawer.svg';
import { ReactComponent as OwnersIcon } from 'assets/icons/owners.svg';
import { ReactComponent as OwnersActiveIcon } from 'assets/icons/owners-active.svg';
import { ReactComponent as EventsIcon } from 'assets/icons/events.svg';
import { ReactComponent as EventsActiveIcon } from 'assets/icons/events-active.svg';
import { ReactComponent as DefinitionsActiveIcon } from 'assets/icons/definitions-active.svg';
import { ReactComponent as DefinitionsIcon } from 'assets/icons/definitions.svg';
import { ReactComponent as GroupsActiveIcon } from 'assets/icons/groups-active.svg';
import { ReactComponent as GroupsIcon } from 'assets/icons/groups.svg';
import { ReactComponent as GlcIcon } from 'assets/icons/logic-group.svg';
import { ReactComponent as GlcActiveIcon } from 'assets/icons/logic-group-active.svg';
import { ReactComponent as ControlActiveIcon } from 'assets/icons/control-active.svg';
import { ReactComponent as ControlIcon } from 'assets/icons/control.svg';
import { ReactComponent as WantedActiveIcon } from 'assets/icons/wanted-active.svg';
import { ReactComponent as WantedIcon } from 'assets/icons/wanted.svg';
import { ReactComponent as AnnouncementsActiveIcon } from 'assets/icons/announcements-active.svg';
import { ReactComponent as AnnouncementsIcon } from 'assets/icons/announcements.svg';
import { ReactComponent as SchedulesActiveIcon } from 'assets/icons/schedules-active.svg';
import { ReactComponent as SchedulesIcon } from 'assets/icons/schedules.svg';
import { ReactComponent as SequencesActiveIcon } from 'assets/icons/sequences-active.svg';
import { ReactComponent as SequencesIcon } from 'assets/icons/sequences.svg';
import { ReactComponent as ReportsIcon } from 'assets/icons/reports-icon.svg';
import { ReactComponent as ReportsActiveIcon } from 'assets/icons/reports-active-icon.svg';
import { useAuth } from 'hooks/useAuth/useAuth';
import { useLocaleStoreQuery } from 'hooks/useLocaleStoreQuery/useLocaleStoreQuery';
import { useResetSelected } from 'hooks/useResetSelected/useResetSelected';
import { AppRoute } from 'routing/AppRoute.enum';
import { LogoContainer } from 'ui/logo/LogoContainer';
import { AppMessages } from 'i18n/messages';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { isDodMonitorEnabled } from 'core/featureFlags';

export const Navbar = () => {
  const { user, isAdmin } = useAuth();
  const { resetQueryState } = useLocaleStoreQuery();
  const { resetAllSelected } = useResetSelected();
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();

  // Zdarzenia hosts three independently permitted tabs; open the first one the user may see.
  const eventsRoute = [
    { route: AppRoute.events, canView: !!perms?.ZDARZENIA?.view },
    { route: AppRoute.eventsOther, canView: !!perms?.ZDARZENIA_INNE?.view },
    { route: AppRoute.eventsCurrentPresence, canView: !!perms?.OBECNOSCI?.view },
  ].find(({ canView }) => canView)?.route;

  const drawerRoutes = [
    {
      icon: <DevicesIcon />,
      activeIcon: <DevicesActiveIcon />,
      to: AppRoute.devices,
      title: formatMessage({ id: AppMessages['nav.devices'] }),
      isVisible: !!perms?.URZADZENIA_GLC?.view,
    },
    {
      icon: <CardsIcon />,
      activeIcon: <CardsActiveIcon />,
      to: AppRoute.cards,
      title: formatMessage({ id: AppMessages['nav.cards'] }),
      isVisible: !!perms?.KARTY_POSIADACZE?.view,
    },
    {
      icon: <OwnersIcon />,
      activeIcon: <OwnersActiveIcon />,
      to: AppRoute.owners,
      title: formatMessage({ id: AppMessages['nav.owners'] }),
      isVisible: !!perms?.KARTY_POSIADACZE?.view,
    },
    {
      icon: <WantedIcon />,
      activeIcon: <WantedActiveIcon />,
      to: AppRoute.wanted,
      title: formatMessage({ id: AppMessages['nav.wanted'] }),
      isVisible: !!perms?.POSZUKIWANY?.view,
    },
    {
      icon: <AnnouncementsIcon />,
      activeIcon: <AnnouncementsActiveIcon />,
      to: AppRoute.announcements,
      title: formatMessage({ id: AppMessages['nav.announcements'] }),
      isVisible: !!perms?.KOMUNIKAT?.view,
    },
    {
      icon: <ControlIcon />,
      activeIcon: <ControlActiveIcon />,
      to: AppRoute.controlRandom,
      title: formatMessage({ id: AppMessages['nav.control'] }),
      isVisible: !!perms?.KONTROLA?.view,
    },
    {
      icon: <SchedulesIcon />,
      activeIcon: <SchedulesActiveIcon />,
      to: AppRoute.schedules,
      title: formatMessage({ id: AppMessages['nav.schedules'] }),
      isVisible: !!perms?.HARMONOGRAMY?.view,
    },
    {
      icon: <EventsIcon />,
      activeIcon: <EventsActiveIcon />,
      to: eventsRoute ?? AppRoute.events,
      title: formatMessage({ id: AppMessages['nav.events'] }),
      isVisible: !!eventsRoute,
    },
    {
      icon: <DefinitionsIcon />,
      activeIcon: <DefinitionsActiveIcon />,
      to: AppRoute.definitions,
      title: formatMessage({ id: AppMessages['nav.definitions'] }),
      isVisible: !!perms?.DEFINICJE?.view,
    },
    {
      icon: <SequencesIcon />,
      activeIcon: <SequencesActiveIcon />,
      to: AppRoute.sequences,
      title: formatMessage({ id: AppMessages['nav.sequences'] }),
      isVisible: !!perms?.SEKWENCJA?.view,
    },
    {
      icon: <GroupsIcon />,
      activeIcon: <GroupsActiveIcon />,
      to: AppRoute.groups,
      title: formatMessage({ id: AppMessages['nav.groups'] }),
      isVisible: !!perms?.UPR_GRUPOWE?.view,
    },
    {
      icon: <GlcIcon />,
      activeIcon: <GlcActiveIcon />,
      to: AppRoute.glc,
      title: formatMessage({ id: AppMessages['nav.glc'] }),
      isVisible: !!perms?.URZADZENIA_GLC?.view,
    },
    {
      icon: <MonitorHeartOutlined sx={{ color: '#A4AAC4', fontSize: 24 }} />,
      activeIcon: <MonitorHeart sx={{ color: '#FFF', fontSize: 24 }} />,
      to: AppRoute.dod,
      title: formatMessage({ id: AppMessages['nav.dod'] }),
      // Admin alone is not the gate: the DOD monitor must not exist on production builds
      // (REACT_APP_DOD_MONITOR=false). The route is guarded by FeatureRoute as well, so a
      // direct URL cannot reach it either.
      isVisible: Boolean(isAdmin) && isDodMonitorEnabled(),
    },
    {
      icon: <UsersIcon />,
      activeIcon: <UsersIcon active />,
      to: AppRoute.users,
      title: formatMessage({ id: AppMessages['nav.users'] }),
      isVisible: Boolean(isAdmin && perms?.UZYTKOWNICY?.view),
    },
    {
      icon: <ReportsIcon />,
      activeIcon: <ReportsActiveIcon />,
      to: AppRoute.reports,
      title: formatMessage({ id: AppMessages['nav.reports'] }),
      isVisible:
        !!perms?.ZESTAWIENIE_UPRAWNIEN?.view ||
        !!perms?.RAPORT_WYDANYCH_KART?.view ||
        !!perms?.RAPORT_POSIADACZY_BEZ_ZDARZEN?.view ||
        !!perms?.RAPORT_ZDARZEN?.view ||
        !!perms?.RAPORT_CZASU_OBECNOSCI?.view ||
        !!perms?.RAPORT_OSOB_OBECNYCH?.view,
    },
  ];

  return (
    <DrawerLayout
      logo={<LogoContainer />}
      drawerRoutes={drawerRoutes}
      profileRoute={{ to: AppRoute.profile, title: user?.username || '' }}
      onClickRoute={() => {
        resetQueryState();
        resetAllSelected();
      }}
    />
  );
};
